import {
  HHAlert,
  HHButton,
  HHCircularProgress,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  DataGridPro,
  GridColumnVisibilityModel,
  GridRowModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDynamicDialogContext } from '../../../context/dynamic-dialog.context';
import { useSnackbarContext } from '../../../context/snackbar.context';
import {
  useBatchCreateProgramCapMutation,
  useGetAllClientsQuery,
} from '../../../types';
import { getBatchImportColumns } from './batch-import-columns';
import {
  BatchImportSummary,
  SubmissionResultRow,
} from './batch-import-summary';
import { BatchImportToolbar } from './batch-import-toolbar';
import { BatchImportRow, validateRow } from './csv-utils';
import { ValidationSummaryBar } from './preview-summary-bar';

export const BatchImport = (): JSX.Element => {
  const [rows, setRows] = useState<BatchImportRow[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [hideSuccessful, setHideSuccessful] = useState(false);
  const [submissionResults, setSubmissionResults] = useState<
    SubmissionResultRow[] | null
  >(null);

  const { setSnackbarState } = useSnackbarContext();
  const { setDialogContent, closeDialog } = useDynamicDialogContext();

  const validationTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const [batchCreateProgramCap, { loading }] =
    useBatchCreateProgramCapMutation();

  const { data: clientsData } = useGetAllClientsQuery();

  const clientNameMap = useMemo(() => {
    if (!clientsData?.getAllClients) return {};
    return clientsData.getAllClients.reduce<Record<number, string>>(
      (acc, client) => {
        acc[client.id] = client.name;
        return acc;
      },
      {},
    );
  }, [clientsData]);

  const runValidation = useCallback(
    async (targetRows: BatchImportRow[]) => {
      if (targetRows.length === 0) return;

      // Client-side validation first — mark invalid rows immediately
      const clientValidated = targetRows.map(row => {
        const { valid, error } = validateRow(row);
        if (!valid) {
          return { ...row, status: 'error' as const, error };
        }
        return row;
      });

      const validRows = clientValidated.filter(row => row.status !== 'error');
      const hasClientErrors = clientValidated.some(
        row => row.status === 'error',
      );

      // If all rows failed client validation, update state and skip the API call
      if (validRows.length === 0) {
        setRows(clientValidated);
        return;
      }

      // Update rows with client-side errors immediately
      if (hasClientErrors) {
        setRows(clientValidated);
      }

      try {
        // ACCOUNTS-AND-PLAN-MIGRATION: row.clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
        const { data } = await batchCreateProgramCap({
          variables: {
            batchCreateProgramCapInput: {
              programCaps: validRows.map(row => ({
                clientId: row.clientId,
                cap: row.cap,
                startDate: row.startDate,
                endDate: row.endDate,
              })),
            },
            dryRun: true,
          },
        });

        if (data?.batchCreateProgramCap) {
          let serverResultIndex = 0;
          setRows(prev =>
            prev.map(row => {
              // Skip rows already marked as client-side errors
              if (row.status === 'error') return row;

              const result = data.batchCreateProgramCap[serverResultIndex];
              serverResultIndex += 1;
              if (!result) return row;
              return {
                ...row,
                status: result.success ? 'success' : 'error',
                error: result.error ?? null,
              };
            }),
          );
        }
      } catch {
        setSnackbarState('Failed to validate', 'error', 3000);
      }
    },
    [batchCreateProgramCap, setSnackbarState],
  );

  const handleRowsImported = useCallback(
    (result: { rows: BatchImportRow[]; errors: string[] }) => {
      setCsvErrors(result.errors);
      if (result.rows.length > 0) {
        setRows(result.rows);
        setHideSuccessful(false);
        runValidation(result.rows);
      }
    },
    [runValidation],
  );

  const handleDeleteRow = useCallback((rowId: string) => {
    setRows(prev => prev.filter(row => row.rowId !== rowId));
  }, []);

  useEffect(() => () => clearTimeout(validationTimerRef.current), []);

  const debouncedRunValidation = useCallback(
    (targetRows: BatchImportRow[]) => {
      clearTimeout(validationTimerRef.current);
      validationTimerRef.current = setTimeout(() => {
        runValidation(targetRows);
      }, 500);
    },
    [runValidation],
  );

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<BatchImportRow>) => {
      const resetRow = { ...newRow, status: undefined, error: undefined };
      const updatedRows = rows.map(row =>
        row.rowId === newRow.rowId ? resetRow : row,
      );
      setRows(updatedRows);
      debouncedRunValidation(updatedRows);
      return newRow;
    },
    [rows, debouncedRunValidation],
  );

  const executeSubmit = useCallback(async () => {
    closeDialog();

    try {
      const { data } = await batchCreateProgramCap({
        variables: {
          batchCreateProgramCapInput: {
            programCaps: rows.map(row => ({
              clientId: row.clientId,
              cap: row.cap,
              startDate: row.startDate,
              endDate: row.endDate,
            })),
          },
          dryRun: false,
        },
        refetchQueries: ['GetProgramCaps'],
      });

      if (data?.batchCreateProgramCap) {
        const results = data.batchCreateProgramCap;
        const resultRows: SubmissionResultRow[] = rows.map((row, index) => {
          const result = results[index];
          return {
            clientId: row.clientId,
            cap: row.cap,
            startDate: row.startDate,
            endDate: row.endDate,
            success: result?.success ?? false,
            error: result?.error ?? null,
          };
        });
        setSubmissionResults(resultRows);
      }
    } catch {
      setSnackbarState('Failed to create program caps', 'error', 3000);
    }
  }, [rows, batchCreateProgramCap, setSnackbarState, closeDialog]);

  const handleSubmit = useCallback(() => {
    setDialogContent({
      title: (
        <HHTypography hhVariant="section-title">
          Confirm Batch Create
        </HHTypography>
      ),
      content: (
        <HHStack spacing={2}>
          <HHTypography hhVariant="body1">
            You are about to create {rows.length} program cap(s). Continue?
          </HHTypography>
        </HHStack>
      ),
      footer: (
        <HHStack direction="row" spacing={2} padding={1}>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            onClick={closeDialog}
          >
            Cancel
          </HHButton>
          <HHButton hhVariant="contained" onClick={executeSubmit}>
            Confirm
          </HHButton>
        </HHStack>
      ),
      width: 'sm',
      fullWidth: true,
    });
  }, [rows.length, setDialogContent, closeDialog, executeSubmit]);

  const handleRetryFailed = useCallback((failedRows: SubmissionResultRow[]) => {
    const retryRows: BatchImportRow[] = failedRows.map(row => ({
      rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      clientId: row.clientId,
      cap: row.cap,
      startDate: row.startDate,
      endDate: row.endDate,
    }));
    setRows(retryRows);
    setHideSuccessful(false);
    setSubmissionResults(null);
  }, []);

  const columns = useMemo(
    () => getBatchImportColumns(handleDeleteRow, clientNameMap),
    [handleDeleteRow, clientNameMap],
  );

  const hasRows = rows.length > 0;
  const validationRan = rows.some(row => row.status !== undefined);
  const allRowsValid = hasRows && rows.every(row => row.status === 'success');
  const submitEnabled = allRowsValid && !loading;

  const columnVisibilityModel: GridColumnVisibilityModel = useMemo(
    () => ({
      status: validationRan,
      error: validationRan,
    }),
    [validationRan],
  );

  const successCount = rows.filter(row => row.status === 'success').length;
  const errorCount = rows.filter(row => row.status === 'error').length;
  const visibleRows =
    validationRan && hideSuccessful
      ? rows.filter(row => row.status !== 'success')
      : rows;

  if (submissionResults) {
    return (
      <BatchImportSummary
        results={submissionResults}
        clientNameMap={clientNameMap}
        onRetryFailed={handleRetryFailed}
      />
    );
  }

  return (
    <HHStack spacing={2} sx={{ padding: 2, height: '100%', overflow: 'auto' }}>
      <BatchImportToolbar
        rowCount={rows.length}
        hasRows={hasRows}
        loading={loading}
        submitEnabled={submitEnabled}
        onRowsImported={handleRowsImported}
        onSubmit={handleSubmit}
      />

      {csvErrors.length > 0 && (
        <HHAlert
          hhVariant="standard"
          severity="error"
          onClose={(): void => setCsvErrors([])}
        >
          {csvErrors.map((error, i) => (
            <div key={i}>{error}</div>
          ))}
        </HHAlert>
      )}

      {loading && hasRows && (
        <HHStack direction="row" spacing={1} alignItems="center">
          <HHCircularProgress size={16} />
          <HHTypography hhVariant="body2" color="text.secondary">
            Validating...
          </HHTypography>
        </HHStack>
      )}

      {validationRan && !loading && hasRows && (
        <ValidationSummaryBar
          successCount={successCount}
          errorCount={errorCount}
          hideSuccessful={hideSuccessful}
          onToggleHideSuccessful={setHideSuccessful}
        />
      )}

      {!hasRows && (
        <HHStack
          alignItems="center"
          justifyContent="center"
          sx={{ paddingY: 8 }}
        >
          <HHTypography hhVariant="body1" color="text.secondary">
            Import a CSV file to get started.
          </HHTypography>
        </HHStack>
      )}

      {hasRows && (
        <DataGridPro
          rows={visibleRows}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          getRowId={(row): string => row.rowId}
          processRowUpdate={processRowUpdate}
          editMode="cell"
          disableRowSelectionOnClick
          sx={{
            minHeight: 400,
            flex: 1,
            '& .MuiDataGrid-cell--editable': {
              cursor: 'text',
            },
          }}
        />
      )}
    </HHStack>
  );
};
